import { OAuth2Config, OAuth2Token } from '../types';
import { TokenExchangeStrategy } from './token-exchange.strategy';

export class StandardTokenExchangeStrategy implements TokenExchangeStrategy {
  protected buildUrlParams(params: Record<string, string | undefined>): string {
    const filtered = Object.entries(params)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`);

    return filtered.join('&');
  }

  async exchangeCodeForToken(
    code: string,
    config: OAuth2Config,
    codeVerifier?: string,
  ): Promise<OAuth2Token> {
    const params: Record<string, string | undefined> = {
      grant_type: 'authorization_code',
      code,
      redirect_uri: config.redirectUri,
      client_id: config.clientId,
    };

    // Handle PKCE
    if ((config.usePKCE || config.pkce) && codeVerifier) {
      params.code_verifier = codeVerifier;
    } else if (config.clientSecret) {
      params.client_secret = config.clientSecret;
    }

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: this.buildUrlParams(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token exchange failed: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();

    // Handle nested response (e.g., Outlook might nest tokens)
    const tokenData = config.responseRootKey ? data[config.responseRootKey] : data;

    const now = Date.now();
    const expiresIn = tokenData.expires_in || 3600;

    return {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token,
      expiresAt: new Date(now + expiresIn * 1000),
      expiresIn: expiresIn,
      tokenType: tokenData.token_type || 'Bearer',
      scope: tokenData.scope,
      createdAt: now,
      raw: data,
    };
  }

  async refreshToken(refreshToken: string, config: OAuth2Config): Promise<OAuth2Token> {
    const params: Record<string, string | undefined> = {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: config.clientId,
    };

    // Only add client_secret if not using PKCE
    if (!(config.usePKCE || config.pkce) && config.clientSecret) {
      params.client_secret = config.clientSecret;
    }

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: this.buildUrlParams(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token refresh failed: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    const tokenData = config.responseRootKey ? data[config.responseRootKey] : data;

    const now = Date.now();
    const expiresIn = tokenData.expires_in || 3600;

    return {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token || refreshToken,
      expiresAt: new Date(now + expiresIn * 1000),
      expiresIn: expiresIn,
      tokenType: tokenData.token_type || 'Bearer',
      scope: tokenData.scope,
      createdAt: now,
      raw: data,
    };
  }
}
